// @flow
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
// Components
import { scroller } from 'react-scroll';
import Slider from 'react-slick';
import { PageLoader } from '../../common';
import HorizontalDivider from '../../home/components/HorizontalDivider';
import ProductSquare from './ProductSquare';
// Types
import type { Product } from '../types';
// Styling
import 'slick-carousel/slick/slick.css';
import './Scroller.css';
//Utils
import { getMinimunScrollerQuantity } from '../utils';

type Props = {
  products: Product[],
  title: string,
  slug: string,
  isLoading: boolean,
  hideBottomLine: boolean,
  scrollable: boolean,
};

const SampleNextArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={style} onClick={onClick}>
      <div className="scroller-arrow-container">
        <i
          className="fa fa-caret-right fa-4x scroller-arrow"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

const SamplePrevArrow = props => {
  const { className, style, onClick } = props;
  return (
    <div className={className} style={style} onClick={onClick}>
      <div className="scroller-arrow-container">
        <i
          className="fa fa-caret-left fa-4x scroller-arrow"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

const Scroller = (props: Props) => {
  const { products, title, slug, isLoading, scrollable } = props;
  const [showAll, setShowAll] = useState(false);
  const [minimunScrollerQuantity, setMinimunScrollerQuantity] = useState(
    getMinimunScrollerQuantity(window.innerWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      setMinimunScrollerQuantity(getMinimunScrollerQuantity(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const prevPropsRef = useRef();

  useEffect(() => {
    if (
      !isLoading &&
      scrollable &&
      products.length > 0 &&
      prevPropsRef.current &&
      !_.isEqual(prevPropsRef.current, props)
    ) {
      scroller.scrollTo(slug, {
        duration: 1500,
        smooth: 'easeInOutQuart',
      });
    }
    prevPropsRef.current = props;
  }, [isLoading, scrollable, products, props, slug]);
  // Note that Product Scroller must have an even number of items if scroll by 2 will behave properly
  const settings = {
    dots: false,
    infinite: props.products.length > minimunScrollerQuantity,
    autoplay: false,
    accessibility: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    arrows: true,
    swipe: true,
    vertical: false,
    variableWidth: true,
    // react-slick 0.15 with variableWidth + infinite renders one extra pre-clone but
    // offsets the track as if it didn't, so index 0 would show the last product first
    initialSlide: props.products.length > minimunScrollerQuantity ? 1 : 0,
    nextArrow:
      props.products.length > minimunScrollerQuantity ? (
        <SampleNextArrow />
      ) : (
        <></>
      ),
    prevArrow:
      props.products.length > minimunScrollerQuantity ? (
        <SamplePrevArrow />
      ) : (
        <></>
      ),
  };

  if (isLoading) {
    return <PageLoader />;
  }
  if (products.length > 0) {
    return (
      <div className="scroller-main">
        <HorizontalDivider title={title} link={slug} id={props.id} />
        <div className="scroller">
          <Slider {...settings}>
            {products.map((product, i) => (
              <div key={i}>
                <ProductSquare
                  productImage={product.images[0]}
                  productName={product.name}
                  productManufacturer={product.manufacturer}
                  productListPrice={product.listPrice}
                  productPrice={product.price}
                  productUnit={product.unitForQuantity}
                  productSlug={product.slug}
                  productId={product.id}
                  parentProductId={product.productId}
                  productTotalOnHand={product.totalOnHand}
                  defaultQuantity={product.defaultQuantity}
                  incrementQuantity={product.incrementQuantity}
                  inStock={product.inStock}
                  discontinued={product.discontinued}
                  isGiftCard={product.isGiftCard}
                  analyticsViewSource={title} // USE ANALYTICS
                />
              </div>
            ))}
          </Slider>
        </div>

        <div className="scroller-mobile">
          <div className="scroller-mobile-products">
            {products.map((product, i) => {
              if (showAll || i < 4) {
                return (
                  <div key={i}>
                    <ProductSquare
                      productImage={product.images[0]}
                      productName={product.name}
                      productManufacturer={product.manufacturer}
                      productListPrice={product.listPrice}
                      productPrice={product.price}
                      productUnit={product.unitForQuantity}
                      productSlug={product.slug}
                      productId={product.id}
                      productTotalOnHand={product.totalOnHand}
                      parentProductId={product.productId}
                      defaultQuantity={product.defaultQuantity}
                      incrementQuantity={product.incrementQuantity}
                      inStock={product.inStock}
                      discontinued={product.discontinued}
                      isGiftCard={product.isGiftCard}
                      analyticsViewSource={title} // USE ANALYTICS
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
          {!showAll && products.length > 4 ? (
            <div className="scroller-mobile-see-more">
              <a
                href="/"
                onClick={e => {
                  e.preventDefault();
                  setShowAll(true);
                }}
              >
                ver mais
              </a>
            </div>
          ) : (
            <div />
          )}
        </div>
      </div>
    );
  } else {
    return <div />;
  }
};

export default Scroller;
